import { generateRequestHandler } from '@studio-toolkit/request-handler';
import { appConfig, TEnv } from 'config/app';
import { TQueryParams } from 'types';

type TApiUrls = {
  [K in TEnv]: string;
};

const getApiUrl = (): string => {
  // Prefer the runtime backend URL injected via public/config.js
  // (window.ENV.VITE_API_URL, sourced from the VITE_API_URL CI/CD variable).
  // In prod this is https://api.eck.spectrumflow.net, so the browser calls the
  // backend host directly. Falls back to the same-origin relative path (served
  // via the nginx /v1 proxy) when the runtime value is absent (e.g. local dev).
  //
  // Normalize to exactly one trailing "/v1": CI/CD variables are inconsistent —
  // some include the /v1 suffix and some do not — and config.js may leave the
  // ${VITE_API_URL} placeholder unsubstituted in local builds. Guard against all
  // of these so the browser always targets <backend>/v1.
  const raw = window.ENV?.VITE_API_URL?.trim();

  // Unsubstituted placeholder or empty:
  //  - local dev (vite dev server): target the local backend directly on :4000
  //  - otherwise (prod/nginx build): same-origin relative path "/v1"
  if (!raw || raw.includes('${')) {
    return import.meta.env.DEV ? 'http://localhost:4000/v1' : '/v1';
  }

  const normalized = raw.replace(/\/+$/, '').replace(/\/v1$/, '');
  return `${normalized}/v1`;
};

/** Replace these with your api urls for desired environments */
export const apiUrls: TApiUrls = {
  prod: getApiUrl(),
  dev: getApiUrl(),
  qa: getApiUrl(),
  uat: getApiUrl(),
  local: getApiUrl(),
};

/** Replace these with endpoints for your api */
export const endpoints = [
  'orderTypes',
  'generatePdf',
  'generateToken',
  'comparePdf',
] as const;

export type TEndpoint = (typeof endpoints)[number];

interface IBaseUrlArgs {
  endpoint?: TEndpoint | string;
  path?: string;
  apiEnv?: keyof TApiUrls;
}

/** Generates an api url based on endpoint, path, and apiEnv.
 * `endpoint` must match value provided in endpoints array from `utils/api.ts`
 * `path` is an extension of an endpoint. For example, to hit `comments/:id` you would pass `{ endpoint: 'comments', path: 'some-id' }`
 * `apiEnv` can be used to select a specific base url from the `apiUrls` object in `utils/api.ts` ('development', 'local', 'production', or 'test'). Defaults to the `env` value of `appConfig`. */
export const apiUrl = ({
  endpoint = '',
  path = '',
  apiEnv = appConfig.env,
}: IBaseUrlArgs) => {
  return `${apiUrls[apiEnv]}/${endpoint}${path ? `/${path}` : ''}`;
};

/** Use to generate consistent query keys based on api endpoint and query params.
 * If no params are provided, the query key will be `[key]`
 * If params are provided, the query key will be `[key, {...params}]` */
export const queryKey = (
  key: TEndpoint,
  params?: TQueryParams & { [key: string]: string | number }
) => (params ? [key, params] : [key]);

/** Formats query params for axios. Pass in as matching option on axios request config. */
export const paramsSerializer = (params: TQueryParams) => {
  const paramArr = Object.entries(params).reduce(
    (acc: string[], [key, value]) => {
      if (typeof value === 'string') {
        value = value.replace(' ', '%20');
      }
      if (value !== undefined) {
        acc.push(`${key}=${value}`);
      }
      return acc;
    },
    []
  );

  return paramArr.join('&');
};

export const { requestHandler, instance: primaryRequestInstance } =
  generateRequestHandler();
